import React, { useEffect, useState } from 'react';
import { Card, Badge, Spinner, Button, Alert, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFetchSubscriptionStatusQuery } from '#store';
import { Link } from 'react-router-dom';
import { Routes } from '#Routes';
import { PlusIcon, ExclamationCircleIcon } from '@heroicons/react/solid';
import moment from 'moment';

const CreditsStatus = () => {
  const { t } = useTranslation();
  const { data, isLoading, isFetching } = useFetchSubscriptionStatusQuery();
  const [prevCredits, setPrevCredits] = useState(null);
  const [showHighlight, setShowHighlight] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Track changes in credits to highlight when they change
  useEffect(() => {
    if (data && prevCredits !== null) {
      // Use new total_available field if available, otherwise fallback to old calculation
      const currentTotal = data.total_available ?? (data.extra_credits + data.creds - data.counts);
      const previousTotal = prevCredits;
      if (currentTotal > previousTotal) {
        // Credits increased - show highlight effect
        setShowHighlight(true);
        // Remove highlight after animation completes
        const timeoutId = setTimeout(() => {
          setShowHighlight(false);
        }, 3000);
        return () => clearTimeout(timeoutId);
      }
    }
    // Update previous credits value
    if (data) {
      // Use new total_available field if available, otherwise fallback to old calculation
      setPrevCredits(data.total_available ?? (data.extra_credits + data.creds - data.counts));
    }
  }, [data, prevCredits]);

  // Show loading spinner when initially loading
  if (isLoading && !data) {
    return (
      <Card border='0' className='shadow p-0 p-md-4 h-100'>
        <Card.Body className='d-flex flex-column justify-content-center align-items-center text-center'>
          <Spinner animation='border' />
        </Card.Body>
      </Card>
    );
  }

  if (!data) return null;

  // Use new total_available field if available, otherwise fallback to old calculation
  const remainingCreds = data.total_available ?? (data.extra_credits + data.creds - data.counts);
  const remainingSubCreds = data.subscription_credits?.available ?? Math.max(0, data.creds - data.counts);
  const extraCredits = data.extra_credits?.total ?? data.extra_credits ?? 0;
  
  // Get credit details with new marketing fields
  const extraCreditsDetails = data.extra_credits?.details || [];
  const oneTimeCreditsDetails = data.one_time_credits?.details || [];
  const subscriptionCreditsDetails = data.subscription_credits?.details || [];
  
  // Find credits expiring soon
  const expiringSoonCredits = extraCreditsDetails.filter(
    (credit) => credit.expires_soon === true
  );
  
  // Map credit sources to readable names
  const getSourceName = (source) => {
    const sourceNames = {
      promotion: t('account.credits-status.source.promotion', 'Promocja'),
      registration: t('account.credits-status.source.registration', 'Rejestracja'),
      admin: t('account.credits-status.source.admin', 'Przyznane przez admina'),
      other: t('account.credits-status.source.other', 'Inne'),
      referral: t('account.credits-status.source.referral', 'Nagroda za polecenie'),
      referral_signup: t('account.credits-status.source.referral_signup', 'Bonus za rejestrację'),
      social_share: t('account.credits-status.source.social_share', 'Udostępnienie'),
      review: t('account.credits-status.source.review', 'Recenzja'),
      survey: t('account.credits-status.source.survey', 'Ankieta'),
      birthday: t('account.credits-status.source.birthday', 'Bonus urodzinowy'),
      loyalty: t('account.credits-status.source.loyalty', 'Program lojalnościowy'),
      milestone: t('account.credits-status.source.milestone', 'Kamień milowy'),
      contest: t('account.credits-status.source.contest', 'Konkurs'),
      partnership: t('account.credits-status.source.partnership', 'Program partnerski'),
    };
    return sourceNames[source] || source;
  };

  // Define highlight animation style
  const highlightStyle = showHighlight
    ? {
        animation: 'pulse 1.5s ease-in-out',
        boxShadow: '0 0 15px rgba(40, 167, 69, 0.7)',
      }
    : {};

  // Add keyframes for pulse animation to document if not already added
  if (showHighlight && !document.getElementById('pulse-animation')) {
    const style = document.createElement('style');
    style.id = 'pulse-animation';
    style.innerHTML = `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  return (
    <Card border='0' className='shadow p-0 p-md-4 h-100' style={highlightStyle}>
      <Card.Body className='d-flex flex-column justify-content-center align-items-center text-center'>
        <h2 className='mb-3'>
          {t('account.credits-status.title')}
          {isFetching && <Spinner animation='border' size='sm' className='ms-2' />}
        </h2>

        {/* Warning about expiring credits */}
        {expiringSoonCredits.length > 0 && (
          <Alert variant='warning' className='mb-3'>
            <ExclamationCircleIcon className='icon icon-xs me-2' />
            <strong>{t('account.credits-status.expiring-warning-title', 'Uwaga!')}</strong>{' '}
            {t('account.credits-status.expiring-warning-text', 'Masz {{count}} kredytów wygasających wkrótce', {
              count: expiringSoonCredits.length,
            })}
          </Alert>
        )}

        <div className='my-3'>
          <div className='mb-3'>
            <h4>{t('account.credits-status.total-credits')}</h4>
            <Badge
              bg='secondary'
              className='p-2 fs-5'
              style={showHighlight ? { animation: 'pulse 1.5s ease-in-out' } : {}}
            >
              {remainingCreds}
            </Badge>
          </div>
          
          {/* Button to show/hide details */}
          {(extraCreditsDetails.length > 0 || oneTimeCreditsDetails.length > 0 || subscriptionCreditsDetails.length > 0) && (
            <Button
              variant='link'
              size='sm'
              onClick={() => setShowDetails(!showDetails)}
              className='text-decoration-none p-0'
            >
              {showDetails
                ? t('account.credits-status.hide-details', 'Ukryj szczegóły')
                : t('account.credits-status.show-details', 'Pokaż szczegóły')}
            </Button>
          )}
        </div>
        
        {/* Credit details */}
        <Collapse in={showDetails}>
          <div>
            {/* Extra Credits Details */}
            {extraCreditsDetails.length > 0 && (
              <div className='mb-3 text-start'>
                <h6 className='text-muted'>{t('account.credits-status.extra-credits-details', 'Kredyty promocyjne/marketingowe')}</h6>
                {extraCreditsDetails.map((credit) => (
                  <div
                    key={credit.id}
                    className={`small mb-2 p-2 rounded ${credit.expires_soon ? 'bg-warning bg-opacity-10' : ''}`}
                  >
                    <div>
                      <strong>{credit.credits_remaining} / {credit.credits}</strong>{' '}
                      <span className='text-muted'>({getSourceName(credit.source)})</span>
                      {credit.campaign_id && (
                        <span className='text-muted ms-2'>• {t('account.credits-status.campaign', 'Kampania')}: {credit.campaign_id}</span>
                      )}
                    </div>
                    <div className='text-muted'>
                      {t('account.credits-status.expires', 'Wygasa')}: {moment(credit.expires).format('DD.MM.YYYY')}
                      {credit.days_until_expiry !== null && (
                        <span> ({credit.days_until_expiry} {t('account.credits-status.days', 'dni')})</span>
                      )}
                      {credit.expires_soon && (
                        <span className='text-warning ms-1'>⚠️ {t('account.credits-status.expires-soon', 'Wygasa wkrótce!')}</span>
                      )}
                    </div>
                    {credit.related_user_email && (
                      <div className='text-muted small'>
                        {t('account.credits-status.related-user', 'Powiązany użytkownik')}: {credit.related_user_email}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* One-time Credits Details */}
            {oneTimeCreditsDetails.length > 0 && (
              <div className='mb-3 text-start'>
                <h6 className='text-muted'>{t('account.credits-status.one-time-credits-details', 'Kredyty zakupione')}</h6>
                {oneTimeCreditsDetails.map((credit) => (
                  <div key={credit.id} className='small mb-2 p-2 rounded'>
                    <div>
                      <strong>{credit.credits_remaining} / {credit.credits}</strong>
                    </div>
                    <div className='text-muted'>
                      {t('account.credits-status.expires', 'Wygasa')}: {moment(credit.expires).format('DD.MM.YYYY')}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Subscription Credits Details */}
            {subscriptionCreditsDetails.length > 0 && (
              <div className='mb-3 text-start'>
                <h6 className='text-muted'>{t('account.credits-status.subscription-credits-details', 'Kredyty z subskrypcji')}</h6>
                {subscriptionCreditsDetails.map((credit) => (
                  <div key={credit.id} className='small mb-2 p-2 rounded'>
                    <div>
                      <strong>{credit.credits_remaining} / {credit.credits}</strong>
                      {credit.sub_type && (
                        <span className='text-muted ms-2'>({credit.sub_type})</span>
                      )}
                    </div>
                    <div className='text-muted'>
                      {t('account.credits-status.period', 'Okres')}: {moment(credit.period_start).format('DD.MM.YYYY')} - {moment(credit.period_end).format('DD.MM.YYYY')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Collapse>

        <Button as={Link} to={Routes.AccountSubscribe.path} variant='logo-green' className='mt-2'>
          <PlusIcon className='icon icon-xs me-2' />
          {t('account.subscription-status.add-credits')}
        </Button>
      </Card.Body>
    </Card>
  );
};

export default React.memo(CreditsStatus);
